import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'
import { getSocket } from '../socket/client.js'

/**
 * Agent-scoped dashboard metrics (GET /agent/dashboard/summary), driven by the
 * My Dashboard date pickers and refreshed in realtime on conversation changes.
 */
export interface AgentDashboardSummary {
  statusCounts: { open: number; closed: number; on_hold: number; reopen: number }
  total: number
  sla: { avgResponseHours: number; avgResolutionHours: number }
  escalated: { response: number; resolution: number }
  sourceCounts: Record<string, number>
  categoryCounts: { label: string; count: number }[]
  subcategoryCounts: { label: string; count: number }[]
  range: { from: string | null; to: string | null }
}

const EMPTY: AgentDashboardSummary = {
  statusCounts: { open: 0, closed: 0, on_hold: 0, reopen: 0 },
  total: 0,
  sla: { avgResponseHours: 0, avgResolutionHours: 0 },
  escalated: { response: 0, resolution: 0 },
  sourceCounts: {},
  categoryCounts: [],
  subcategoryCounts: [],
  range: { from: '', to: '' },
}

export interface UseAgentDashboardResult {
  data: AgentDashboardSummary
  loading: boolean
  refetch: () => Promise<void>
}

export function useAgentDashboard(from: string, to: string, statuses?: string[]): UseAgentDashboardResult {
  const [data, setData] = useState<AgentDashboardSummary>(EMPTY)
  const [loading, setLoading] = useState(true)
  // Joined once so a rebuilt array doesn't restart the fetch loop.
  const statusKey = statuses?.join(',')

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true)
    try {
      const p = new URLSearchParams()
      if (from) p.set('from', from)
      if (to) p.set('to', to)
      if (statusKey !== undefined) p.set('statuses', statusKey)
      const res = await api.get<AgentDashboardSummary>(`/agent/dashboard/summary?${p.toString()}`)
      setData(res)
    } catch {
      setData(EMPTY)
    } finally {
      setLoading(false)
    }
  }, [from, to, statusKey])

  useEffect(() => { void refetch() }, [refetch])

  // Realtime: the tenant room bumps `conversation:list:bump` on any create /
  // update / status change — debounce-refresh so the dashboard stays live.
  useEffect(() => {
    const socket = getSocket()
    let t: ReturnType<typeof setTimeout> | null = null
    const onBump = (): void => {
      if (t) clearTimeout(t)
      t = setTimeout(() => void refetch(), 600)
    }
    socket.on('conversation:list:bump', onBump)
    return () => {
      if (t) clearTimeout(t)
      socket.off('conversation:list:bump', onBump)
    }
  }, [refetch])

  return { data, loading, refetch }
}
